// This file is largely based off of @rollbar/react's implementation of ErrorBoundary.
// https://github.com/rollbar/rollbar-react/blob/ee66c622b2f3c02d5bbe199f90a99b66ec89f4ab/src/error-boundary.js
// The main differences are:
// 1. We do not use React.Context for managing the Rollbar instance. Instead, we use a singleton
//    initialized at the root of the project. This limits child implementations of Rollbar instances.
//    However, unlike Context with hooks, the singleton allows Sagas to emit Rollbar events.
// 2. We do not log when `NODE_ENV === 'development'`
// 3. We have NewRelic support
// 4. We have a default fallback instead of `null`
// 5. We do not support the `extra` prop since it was not necessary at the time of our implementation
// 6. We have TypeScript support
import { Component, ErrorInfo, ReactNode } from 'react';

import * as React from 'react';
import { getRollbar, LEVEL, LEVEL_ERROR } from '../../rollbar';

export interface ErrorBoundaryProps {
  fallbackUI?: ReactNode | ((error: Error, resetError: () => void) => ReactNode);
  level?: LEVEL;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

const INITIAL_ERROR_STATE = { hasError: false, error: null } as const;

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps = {
    level: LEVEL_ERROR,
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { ...INITIAL_ERROR_STATE };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Catch errors in any child components and re-render with error message
    const { level = LEVEL_ERROR } = this.props;
    if (process.env.NODE_ENV === 'development') return;
    const nrWindow = window.newrelic;
    if (nrWindow && typeof nrWindow.noticeError === 'function') nrWindow.noticeError(error, { componentStack: errorInfo.componentStack });
    getRollbar()[level](error, errorInfo);
  }

  resetError = (): void => {
    this.setState(INITIAL_ERROR_STATE);
  };

  render(): ReactNode {
    const { children, fallbackUI } = this.props;
    const { hasError, error } = this.state;
    if (!hasError || !error) return children;
    if (React.isValidElement(fallbackUI)) {
      const FallbackUI = fallbackUI as unknown as React.ElementType;
      return <FallbackUI error={error} resetError={this.resetError} />;
    }
    if (typeof fallbackUI === 'function') {
      const fallbackComponent = fallbackUI(error, this.resetError);
      return React.isValidElement(fallbackComponent) ? fallbackComponent : null;
    }
    // Unlike @rollbar/react, we have a default fallback instead of `null`
    // If the consumer wants an empty UI, they can pass in `<></>` as the `fallbackUI`.
    return (
      <div style={{ padding: 40 }}>
        <h1>Something went wrong.</h1>
        <h3>Try refreshing the page.</h3>
        {process.env.NODE_ENV !== 'production' && error.message && <p>{error.message}</p>}
        <h3>If the problem persists, please contact support.</h3>
      </div>
    );
  }
}
